// OptiGrowth visitor identity — the durable anonymous browser id (`og_vid_v1`)
// that lets the server-side OptiGrowth decider randomize on visitors BEFORE an
// account exists (pre-auth surfaces like /signUp). Value format is
// `<uuid-v4>.<mintedAtEpochMs>`; the mint timestamp makes conflicts resolvable
// (oldest wins = the earliest first touch survives).
//
// Dual storage: host-only cookie + localStorage mirror under the same key, so
// the id survives whichever store the browser clears first. Cookie attributes
// mirror lib/referral-cookies.ts (host-only, non-HttpOnly, SameSite=Lax,
// Path=/), plus `Secure` off-localhost and a 400-day Max-Age (Chrome's cap).
// Server-side durability (Safari ITP) is handled by the resolve response's
// Set-Cookie — this module is the client half only.
//
// Every storage access is wrapped in try/catch: a throwing store (private
// mode, quota, cookies disabled) is treated as absent, and when BOTH stores
// are unusable we return null — the caller renders the default experience.
// This module must never throw.
//
// Spec: claude-dev/kate/2026-07-21-optigrowth-visitor-experiments/ (harness
// root), §1-V1.

const STORE_KEY = 'og_vid_v1';
// 400 days — the maximum cookie lifetime Chrome honors.
const MAX_AGE_SECONDS = 400 * 24 * 60 * 60;
// Hard cap on the whole `<uuid>.<mintedAtMs>` value; anything longer is
// hostile/garbage and treated as absent (also enforced server-side).
const MAX_VALUE_LENGTH = 64;
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface ParsedVisitorId {
  value: string;
  mintedAt: number;
}

/**
 * Validates a raw stored value. Anything that isn't exactly
 * `<uuid-v4>.<positive integer ms>` within the length cap is treated as
 * ABSENT — never used, never written back, never sent to the server.
 */
function parseVisitorId(raw: string | null): ParsedVisitorId | null {
  if (!raw || raw.length > MAX_VALUE_LENGTH) return null;
  const separator = raw.indexOf('.');
  if (separator === -1) return null;
  const uuid = raw.slice(0, separator);
  const mintedAtRaw = raw.slice(separator + 1);
  if (!UUID_V4_PATTERN.test(uuid)) return null;
  if (!/^\d+$/.test(mintedAtRaw)) return null;
  const mintedAt = Number(mintedAtRaw);
  if (!Number.isFinite(mintedAt) || mintedAt <= 0) return null;
  return { value: raw, mintedAt };
}

interface StoreRead {
  raw: string | null;
  /** False when the store itself threw — distinct from "readable but empty". */
  usable: boolean;
}

function readCookieStore(): StoreRead {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${STORE_KEY}=([^;]*)`));
    if (!match) return { raw: null, usable: true };
    try {
      return { raw: decodeURIComponent(match[1]), usable: true };
    } catch {
      // Malformed encoding = garbage value, but the store itself works.
      return { raw: null, usable: true };
    }
  } catch {
    return { raw: null, usable: false };
  }
}

function readLocalStorageStore(): StoreRead {
  try {
    return { raw: window.localStorage.getItem(STORE_KEY), usable: true };
  } catch {
    return { raw: null, usable: false };
  }
}

function isLocalhost(): boolean {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

function writeCookieStore(value: string): void {
  try {
    // Host-only (no domain=), Secure only off-localhost — a Secure cookie on
    // plain-http localhost would be silently dropped by the browser.
    const secure = isLocalhost() ? '' : '; Secure';
    document.cookie = `${STORE_KEY}=${encodeURIComponent(value)}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
  } catch {
    // Unusable store — degrade silently; the mirror may still hold the id.
  }
}

function writeLocalStorageStore(value: string): void {
  try {
    window.localStorage.setItem(STORE_KEY, value);
  } catch {
    // Quota/private mode — degrade silently; the cookie may still hold the id.
  }
}

function mintVisitorId(): string | null {
  try {
    return `${crypto.randomUUID()}.${Date.now()}`;
  } catch {
    // No crypto.randomUUID (ancient browser / non-secure context) — default
    // experience rather than a weak or throwing id.
    return null;
  }
}

/**
 * Returns the durable visitor id, minting one when no valid id exists.
 *
 * Conflict rules (spec §1-V1, exactly — no other case mints or writes):
 * - both stores valid and equal   → use it, no writes
 * - both valid but differ         → OLDEST `mintedAt` wins, winner written
 *                                   back to BOTH stores
 * - only one valid                → use it and restore the other store
 * - neither valid (absent/garbage)→ mint new, write both
 * - both stores throwing          → null (default experience, never throw)
 */
export function getOrMintVisitorId(): string | null {
  const cookieRead = readCookieStore();
  const lsRead = readLocalStorageStore();
  if (!cookieRead.usable && !lsRead.usable) return null;

  const fromCookie = parseVisitorId(cookieRead.raw);
  const fromLs = parseVisitorId(lsRead.raw);

  if (fromCookie && fromLs && fromCookie.value === fromLs.value) {
    return fromCookie.value;
  }

  if (fromCookie && fromLs) {
    // Both valid but different ids: the OLDEST mint wins (earliest first
    // touch), and the winner is written back to both stores so they converge.
    const winner = fromCookie.mintedAt <= fromLs.mintedAt ? fromCookie : fromLs;
    writeCookieStore(winner.value);
    writeLocalStorageStore(winner.value);
    return winner.value;
  }

  const survivor = fromCookie ?? fromLs;
  if (survivor) {
    // Only one store survived: use it and restore the other. Rewriting the
    // source store too is harmless and refreshes the cookie's Max-Age window.
    writeCookieStore(survivor.value);
    writeLocalStorageStore(survivor.value);
    return survivor.value;
  }

  const minted = mintVisitorId();
  if (minted === null) return null;
  writeCookieStore(minted);
  writeLocalStorageStore(minted);
  return minted;
}

/**
 * Read-only lookup for the signup payload: the current visitor id or null.
 * Never mints and never writes — signup must not create a fresh id for a
 * visitor whose stores were empty moments before registering.
 */
export function getExistingVisitorId(): string | null {
  const fromCookie = parseVisitorId(readCookieStore().raw);
  const fromLs = parseVisitorId(readLocalStorageStore().raw);
  if (fromCookie && fromLs) {
    return fromCookie.mintedAt <= fromLs.mintedAt ? fromCookie.value : fromLs.value;
  }
  return (fromCookie ?? fromLs)?.value ?? null;
}
